import { Component } from "react"
import { v4 as uuid } from "uuid"
import PropTypes from "prop-types"
import classNames from "classnames"
import { connect } from "react-redux"
import { find, get, chain, isNil, omit, last } from "lodash"
import moment from "moment"
import resizable from "hocs/resizable"
import tooltipContainer from "hocs/tooltip-container"
import Tooltip from "components/charts/tooltip"
import { DATE } from "helpers/shapes"
import { LINE, HIGHLIGHTER } from "../shapes"
import LegendItem from "./legend-item"
import ChartDefs from "./components/chart-defs"
import Axis from "./axis"
import Dots from "./dots"
import { getData, getLines, getXAxis, getRightYAxis, getLeftYAxis, getScaleX, getRightScaleY, getLeftScaleY, getFindByOffset, getHighlighter, getOnlyLegendText } from "./helper"
import "./style.scss"

const AXIS = PropTypes.shape({
  fn: PropTypes.func.isRequired,
  transform: PropTypes.string.isRequired,
  baseline: PropTypes.shape({
    x1: PropTypes.number.isRequired,
    x2: PropTypes.number.isRequired,
    y1: PropTypes.number.isRequired,
    y2: PropTypes.number.isRequired,
  }).isRequired,
})

class MultiLineDoubleYAxis extends Component {
  static propTypes = {
    lines: PropTypes.arrayOf(LINE).isRequired,
    data: PropTypes.array.isRequired,
    highlighter: HIGHLIGHTER,
    startDate: DATE,
    endDate: DATE,
    height: PropTypes.number.isRequired,
    width: PropTypes.number,
    resizableRef: PropTypes.func.isRequired,
    xAxis: AXIS.isRequired,
    leftYAxis: AXIS.isRequired,
    rightYAxis: AXIS.isRequired,
    leftAxisTitle: PropTypes.string,
    rightAxisTitle: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    })).isRequired,
    tooltip: PropTypes.element.isRequired,
    scaleX: PropTypes.func.isRequired,
    scaleY: PropTypes.func.isRequired,
    scaleYTotal: PropTypes.func.isRequired,
    findByOffset: PropTypes.func.isRequired,
    toggle: PropTypes.func.isRequired,
    subscribe: PropTypes.func.isRequired,
    unsubscribe: PropTypes.func.isRequired,
    setDate: PropTypes.func.isRequired,
    focusChartLine: PropTypes.func.isRequired,
    toggleOnly: PropTypes.func.isRequired,
    blurChartLine: PropTypes.func.isRequired,
    focusedChartLine: PropTypes.string,
    onlyLegendText: PropTypes.string.isRequired,
  }

  legendRef = null

  constructor(props) {
    super(props)

    const chartId = uuid()
    this.pathGradientId = `path-gradient-${chartId}`
    this.axisGradientId = `axis-gradient-${chartId}`
  }

  findByOffset = offset => {
    const { data, scaleX } = this.props
    const lookup = scaleX.invert(offset - 18) // -2 is to give a margin of selection on the first element, -16 for the margin
    return find(data, datum => datum.date >= lookup) || last(data)
  }

  findDots = offset => {
    const { scaleX, scaleY, scaleYTotal, items, findByOffset } = this.props

    const datum = findByOffset(offset)

    if (datum) {
      return {
        x: scaleX(datum.date),
        dots: items
          .filter(item => !item.disabled && !isNil(datum[item.id]))
          .map(item => ({
            id: item.id,
            color: item.color,
            y: item.id === "total" ? scaleYTotal(get(datum, [item.id], 0)) : scaleY(get(datum, [item.id], 0)),
          })),
      }
    }
  }

  setDate = offset => {
    const { setDate } = this.props
    const { date } = this.findByOffset(offset)
    setDate(moment(date).format("YYYY-MM-DD"))
  }

  handleGraphClick = ev => {
    const box = ev.currentTarget.getBoundingClientRect()
    const offsetX = ev.pageX - box.left + 16
    this.setDate(offsetX)
  }

  computeAxisStyle = () => {
    const { highlighter, width } = this.props
    const axisStyle = {}

    if (highlighter && width) {
      axisStyle.stroke = `url(#${this.axisGradientId})`
    }

    return axisStyle
  }

  computePathStyle = (id, initialStyle) => {
    const { highlighter, width } = this.props
    const pathStyle = {}

    if (highlighter && width) {
      pathStyle.stroke = `url(#${id})`
    } else {
      pathStyle.stroke = initialStyle
    }

    return pathStyle
  }

  registerLegendRef = element => { this.legendRef = element }

  render() {
    const {
      data,
      resizableRef,
      lines,
      xAxis,
      leftYAxis,
      rightYAxis,
      leftAxisTitle,
      rightAxisTitle,
      items,
      toggle,
      focusedChartLine,
      focusChartLine,
      blurChartLine,
      subscribe,
      unsubscribe,
      highlighter,
      tooltip,
      width,
      toggleOnly,
      onlyLegendText,
    } = this.props

    if (!data.length) {
      return <div ref={resizableRef} className="components_charts_multi-line-double-y-axis no-data">No significant data was found. Adjust the date range to a larger time frame to view graph.</div>
    } else {
      return <div className="components_charts_multi-line-double-y-axis">
        <div className="legend" ref={this.registerLegendRef}>
          {items.map(item =>
            <LegendItem
              onlyLegendText={onlyLegendText}
              key={item.id}
              {...item}
              onClick={toggle}
              onOnlyToggle={toggleOnly}
              focus={focusChartLine}
              blur={blurChartLine}
              focused={focusedChartLine}
            />,
          )}
        </div>
        <svg ref={resizableRef} onClick={this.handleGraphClick} className="multi-line-chart">
          <ChartDefs lines={lines} axisId={this.axisGradientId} pathsId={this.pathGradientId} offset={highlighter && highlighter.x1} chartWidth={width} />
          <Axis {...xAxis} />
          <Axis {...leftYAxis} title={leftAxisTitle} position="left" />
          <Axis {...rightYAxis} title={rightAxisTitle} position="right" />
          <line {...xAxis.baseline} className="baseline x" style={this.computeAxisStyle()} />
          <line {...leftYAxis.baseline} className="baseline left" />
          <line {...rightYAxis.baseline} className="baseline right" />

          {/* Display hidden lines first so that the actual lines are over them */}
          {chain(lines).filter(l => l.missing).map((line) =>
            <path
              key={`${line.id}-missing`}
              className={classNames(
                "path missing",
                focusedChartLine && focusedChartLine !== line.id && "hidden",
              )}
              d={line.missing}
            />,
          ).value()}

          {lines.map((line, index) => <path
            key={line.id}
            className={classNames(
              "path focus-enabled",
              {
                visible: focusedChartLine && focusedChartLine === line.id,
                hidden: focusedChartLine && focusedChartLine !== line.id,
                "single-dot": data.length === 1,
                dashed: line.dashed,
              },
            )}
            {...omit(line, "dashed")}
            style={this.computePathStyle(`${this.pathGradientId}-${index}`, line.style.stroke)}
          />,
          )}
          {highlighter && <line className="highlighter" {...highlighter} />}
          <Dots
            {...{
              subscribe,
              unsubscribe,
              findDots: this.findDots,
              focus: focusChartLine,
              blur: blurChartLine,
              focused: focusedChartLine,
            }}
          />
        </svg>
        <Tooltip {...{ legendRef: this.legendRef, subscribe, unsubscribe, findByOffset: this.findByOffset, position: "right" }}>
          {tooltip}
        </Tooltip>
      </div>
    }
  }
}

const mapStateToProps = (state, props) => ({
  data: getData(props),
  lines: getLines(props),
  xAxis: getXAxis(props),
  rightYAxis: getRightYAxis(props),
  leftYAxis: getLeftYAxis(props),
  scaleX: getScaleX(props),
  scaleY: getRightScaleY(props),
  scaleYTotal: getLeftScaleY(props),
  findByOffset: getFindByOffset(props),
  highlighter: getHighlighter(props),
  onlyLegendText: getOnlyLegendText(props),
})

export default tooltipContainer(resizable(connect(mapStateToProps)(MultiLineDoubleYAxis)))
